import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth, AuthProvider } from './context/AuthContext';
import PinLogin from './components/PinLogin';
import OrderTaker from './components/OrderTaker';
import KitchenDisplay from './components/KitchenDisplay';
import AdminPanel from './components/AdminPanel';
import CustomerBoard from './components/CustomerBoard';
import SuperAdminView from './components/SuperAdminView';
import WildcraftLogo from './components/WildcraftLogo';
import api from './utils/api';
import './App.css';

function SplashScreen({ onComplete }) {
  const [phase, setPhase] = useState('enter'); // enter, hold, exit

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('hold'), 800);
    const t2 = setTimeout(() => setPhase('exit'), 3500);
    const t3 = setTimeout(onComplete, 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center bg-[#0D1F0D] transition-opacity duration-400 ${phase === 'exit' ? 'opacity-0' : 'opacity-100'}`} data-testid="splash-screen">
      <div className={`transition-all duration-700 ${phase === 'enter' ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`}>
        <WildcraftLogo size={100} className="mx-auto animate-logo-breathe" />
      </div>
      <h1 className={`font-brand text-4xl sm:text-5xl font-bold text-white tracking-tight mt-6 transition-all duration-700 delay-200 ${phase === 'enter' ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
        Wildcraft Gatherings
      </h1>
      <p className={`font-mono text-xs text-[#7D8B69] uppercase tracking-[0.3em] mt-3 transition-all duration-700 delay-400 ${phase === 'enter' ? 'opacity-0' : 'opacity-100'}`}>
        Pop-Up Kitchen
      </p>
      <div className={`mt-8 flex gap-2 transition-all duration-500 delay-500 ${phase === 'enter' ? 'opacity-0' : 'opacity-100'}`}>
        <span className="w-2 h-2 bg-[#4A7C3F] rounded-full animate-bounce-dot" style={{animationDelay: '0ms'}}></span>
        <span className="w-2 h-2 bg-[#4A7C3F] rounded-full animate-bounce-dot" style={{animationDelay: '150ms'}}></span>
        <span className="w-2 h-2 bg-[#4A7C3F] rounded-full animate-bounce-dot" style={{animationDelay: '300ms'}}></span>
      </div>
    </div>
  );
}

function AuthContent() {
  const { user, loading } = useAuth();
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    if (!user || ['super_admin', 'admin'].includes(user.role)) return;
    const viewByRole = {
      cook: 'Kitchen Display',
      order_taker: 'Order Taker',
    };
    api.post('/auth/session/activity', { current_view: viewByRole[user.role] || 'Active' }).catch(() => {});
  }, [user]);

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1F0D]" data-testid="loading-screen">
        <div className="text-center">
          <WildcraftLogo size={80} className="mx-auto animate-logo-breathe" />
          <h1 className="font-brand text-3xl font-bold text-white tracking-tight mt-4 animate-fade-in">Wildcraft Gatherings</h1>
          <div className="mt-4 flex justify-center gap-1.5">
            <span className="w-2 h-2 bg-[#4A7C3F] rounded-full animate-bounce-dot" style={{animationDelay: '0ms'}}></span>
            <span className="w-2 h-2 bg-[#4A7C3F] rounded-full animate-bounce-dot" style={{animationDelay: '150ms'}}></span>
            <span className="w-2 h-2 bg-[#4A7C3F] rounded-full animate-bounce-dot" style={{animationDelay: '300ms'}}></span>
          </div>
        </div>
      </div>
    );
  }

  if (!user) return <PinLogin />;

  switch (user.role) {
    case 'super_admin': return <SuperAdminView />;
    case 'admin': return <SuperAdminView />;
    case 'cook': return <KitchenDisplay />;
    case 'order_taker': return <OrderTaker />;
    default: return <PinLogin />;
  }
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/board" element={<CustomerBoard />} />
          <Route path="*" element={<AuthContent />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;

